import { useCallback, useEffect, useRef } from 'react';
import {
  engagementsApi,
  EngagementType,
  EngagementWatchStatus,
} from 'src/api/engagements';
import { VIDEO_ENGAGEMENT_CONSTANTS } from 'src/const/constants';

interface VideoEngagementConfig {
  postId: string;
  minWatchDuration?: number; // 2 seconds to trigger first engagement
  nearCompletionThreshold?: number; // 2 seconds before end to trigger completion
}

export const useVideoEngagement = (config: VideoEngagementConfig) => {
  const {
    postId,
    minWatchDuration = VIDEO_ENGAGEMENT_CONSTANTS.watchStartThreshold,
    nearCompletionThreshold = VIDEO_ENGAGEMENT_CONSTANTS.watchEndOffsetFromEnd,
  } = config;

  const hasReportedMinWatchRef = useRef<boolean>(false);
  const hasReportedNearCompletionRef = useRef<boolean>(false);

  const sendEngagement = useCallback(
    (currentTime: number, watchStatus: EngagementWatchStatus) => {
      if (!postId) {
        return;
      }

      const payload = {
        type: EngagementType.CHALLENGE_VIEWED,
        duration: currentTime * 1000, // backend use milliseconds
        postId,
        watchStatus,
      };

      engagementsApi
        .upsertWatchedEngagements(payload)
        .catch((err) =>
          console.error('Failed to record video engagement:', err)
        );
    },
    [postId]
  );

  const createHandlers = useCallback(
    (videoRef: React.RefObject<HTMLVideoElement>) => {
      const handleTimeUpdate = () => {
        const video = videoRef.current;
        if (!video) {
          return;
        }

        const currentTime = video.currentTime;
        const duration = video.duration;

        // Check if user has reached minimum watch time
        if (
          !hasReportedMinWatchRef.current &&
          currentTime >= minWatchDuration
        ) {
          hasReportedMinWatchRef.current = true;
          sendEngagement(currentTime, EngagementWatchStatus.IN_PROGRESS);
        }

        // Check if video is near completion
        if (
          !hasReportedNearCompletionRef.current &&
          duration > 0 &&
          duration - currentTime <= nearCompletionThreshold &&
          hasReportedMinWatchRef.current
        ) {
          hasReportedNearCompletionRef.current = true;
          sendEngagement(currentTime, EngagementWatchStatus.COMPLETE);
        }
      };

      const handleEnded = () => {
        const video = videoRef.current;
        if (!video) {
          return;
        }

        if (
          !hasReportedNearCompletionRef.current &&
          hasReportedMinWatchRef.current
        ) {
          sendEngagement(video.currentTime, EngagementWatchStatus.COMPLETE);
        }

        // Reset for next play session
        hasReportedMinWatchRef.current = false;
        hasReportedNearCompletionRef.current = false;
      };

      return {
        handleTimeUpdate,
        handleEnded,
      };
    },
    [sendEngagement, minWatchDuration, nearCompletionThreshold]
  );

  useEffect(() => {
    hasReportedMinWatchRef.current = false;
    hasReportedNearCompletionRef.current = false;
  }, [postId]);

  return {
    createHandlers,
  };
};
